(() => {
  'use strict';
  if (window.__ROCKY_RK9_CONVERSION__) return;
  window.__ROCKY_RK9_CONVERSION__ = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const favoriteKey = 'rocky-favorites-v1';
  const productCache = new Map();
  let favoritesDialog;
  let featuredPromise;
  let mediaObserver;
  let mediaVisibilityObserver;
  let cartDialogObserver;
  let observedCartDialog;
  let mediaVisibilityReady = false;
  let mediaSyncQueued = false;

  const readFavorites = () => {
    try {
      const handles = JSON.parse(localStorage.getItem(favoriteKey) || '[]');
      return Array.isArray(handles) ? [...new Set(handles.filter(Boolean))] : [];
    } catch {
      return [];
    }
  };

  const writeFavorites = handles => {
    localStorage.setItem(favoriteKey, JSON.stringify([...new Set(handles.filter(Boolean))]));
  };

  const money = cents => new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'TRY',
    minimumFractionDigits: Number(cents) % 100 ? 2 : 0,
    maximumFractionDigits: 2
  }).format(Number(cents || 0) / 100);

  const collectionMoney = value => {
    const amount = Number(value || 0);
    const cents = typeof value === 'string' && value.includes('.')
      ? Math.round(amount * 100)
      : amount;
    return money(cents);
  };

  const imageUrl = product => {
    const image = product?.featured_image || product?.images?.[0];
    return typeof image === 'string' ? image : image?.src || '';
  };

  const fetchProduct = handle => {
    if (!productCache.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      productCache.set(handle, fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      }).then(response => {
        if (!response.ok) throw new Error('product');
        return response.json();
      }));
    }
    return productCache.get(handle);
  };

  const syncFavoriteSurfaces = () => {
    const handles = readFavorites();
    const selected = new Set(handles);
    document.querySelectorAll('[data-rk5-favorite]').forEach(button => {
      const active = selected.has(button.dataset.handle || '');
      button.setAttribute('aria-pressed', String(active));
      button.classList.toggle('is-active', active);
    });
    document.querySelectorAll('[data-rk9-favorites-count]').forEach(node => {
      const count = String(handles.length);
      if (node.textContent !== count) node.textContent = count;
      node.hidden = handles.length === 0;
    });
    return handles;
  };

  const favoriteCard = product => {
    const article = document.createElement('article');
    article.className = 'rk9-favorite-card';
    const href = product.url || `/products/${product.handle}`;

    const media = document.createElement('a');
    media.className = 'rk9-favorite-card__media';
    media.href = href;
    if (imageUrl(product)) {
      const image = document.createElement('img');
      image.src = imageUrl(product);
      image.alt = product.title || '';
      image.loading = 'lazy';
      media.append(image);
    }

    const copy = document.createElement('div');
    copy.className = 'rk9-favorite-card__copy';
    const eyebrow = document.createElement('small');
    eyebrow.textContent = product.vendor || 'Rocky Sport';
    const title = document.createElement('a');
    title.href = href;
    title.textContent = product.title || 'Rocky';
    const price = document.createElement('strong');
    price.textContent = money(product.price);
    copy.append(eyebrow, title, price);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'rk9-favorite-card__remove';
    remove.dataset.rk9FavoriteRemove = product.handle;
    remove.setAttribute('aria-label', `${product.title || '\u00dcr\u00fcn'} favorilerden \u00e7\u0131kar`);
    remove.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
    article.append(media, copy, remove);
    return article;
  };

  const renderFavorites = async () => {
    if (!favoritesDialog) return;
    const grid = favoritesDialog.querySelector('[data-rk9-favorites-grid]');
    const empty = favoritesDialog.querySelector('[data-rk9-favorites-empty]');
    const handles = syncFavoriteSurfaces();
    grid.replaceChildren();
    empty.hidden = handles.length > 0;
    if (!handles.length) return;
    grid.setAttribute('aria-busy', 'true');
    const products = await Promise.all(handles.map(handle => fetchProduct(handle).catch(() => null)));
    products.filter(Boolean).forEach(product => grid.append(favoriteCard(product)));
    grid.removeAttribute('aria-busy');
    empty.hidden = grid.children.length > 0;
  };

  const buildFavoritesDialog = () => {
    if (favoritesDialog?.isConnected) return favoritesDialog;
    const dialog = document.createElement('dialog');
    dialog.className = 'rk9-favorites';
    dialog.setAttribute('aria-labelledby', 'RockyFavoritesTitle');
    dialog.innerHTML = `
      <div class="rk9-favorites__shell">
        <header class="rk9-favorites__header">
          <div><small>ROCKY L\u0130STEM</small><h2 id="RockyFavoritesTitle">Favorilerim</h2></div>
          <button type="button" data-rk9-favorites-close aria-label="Favorileri kapat">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </header>
        <div class="rk9-favorites__body">
          <div class="rk9-favorites__empty" data-rk9-favorites-empty>
            <strong>Hen\u00fcz favorin yok.</strong>
            <p>Be\u011fendi\u011fin Rocky\u2019leri kalp simgesine dokunarak burada biriktirebilirsin.</p>
            <a href="/collections/yeni-sezon">Yeni sezonu ke\u015ffet</a>
          </div>
          <div class="rk9-favorites__grid" data-rk9-favorites-grid></div>
        </div>
      </div>
    `;
    document.body.append(dialog);
    favoritesDialog = dialog;
    dialog.querySelector('[data-rk9-favorites-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => {
      if (event.target === dialog) dialog.close();
      const remove = event.target.closest('[data-rk9-favorite-remove]');
      if (!remove) return;
      writeFavorites(readFavorites().filter(handle => handle !== remove.dataset.rk9FavoriteRemove));
      renderFavorites();
    });
    return dialog;
  };

  const favoriteButtonMarkup = label => `
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.7-7.5 1.1-1.1a5.5 5.5 0 0 0 0-7.8Z"/></svg>
    <span>${label}</span><b data-rk9-favorites-count hidden>0</b>
  `;

  const initFavorites = () => {
    const header = document.querySelector('.rk4-header__right');
    if (header && !header.querySelector('[data-rk9-favorites-open]')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rk4-icon rk9-favorites-trigger';
      button.dataset.rk9FavoritesOpen = '';
      button.setAttribute('aria-label', 'Favorilerimi g\u00f6r\u00fcnt\u00fcle');
      button.innerHTML = favoriteButtonMarkup('Favoriler');
      header.querySelector('search-button')?.insertAdjacentElement('beforebegin', button);
    }

    const utilities = document.querySelector('.rk4-mobile-menu__utilities');
    if (utilities && !utilities.parentElement.querySelector('.rk9-menu-favorites')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rk9-menu-favorites';
      button.dataset.rk9FavoritesOpen = '';
      button.innerHTML = favoriteButtonMarkup('Favorilerim');
      utilities.insertAdjacentElement('afterend', button);
    }
    syncFavoriteSurfaces();
  };

  const initFooter = () => {
    document.querySelectorAll('.rk4-footer:not([data-rk9-footer-ready])').forEach(footer => {
      footer.dataset.rk9FooterReady = 'true';
      const menus = [...footer.querySelectorAll('.rk4-footer__menu')];
      const desktop = window.matchMedia('(min-width: 721px)');
      const sync = () => {
        if (desktop.matches) menus.forEach(menu => { menu.open = true; });
      };
      sync();
      desktop.addEventListener?.('change', sync);

      const icons = {
        instagram: '<svg data-rk9-social-icon viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="3.5" width="17" height="17" rx="4.5"/><circle cx="12" cy="12" r="3.75"/><path d="M17.6 6.5h.01"/></svg>',
        tiktok: '<svg data-rk9-social-icon viewBox="0 0 24 24" aria-hidden="true"><path d="M15 4v10.25a4.25 4.25 0 1 1-3.5-4.18"/><path d="M15 4c.55 2.42 2.05 3.9 4.35 4.4"/></svg>',
        youtube: '<svg data-rk9-social-icon viewBox="0 0 24 24" aria-hidden="true"><path d="M20.3 7.2c-.2-.8-.8-1.4-1.6-1.6C17.3 5.2 12 5.2 12 5.2s-5.3 0-6.7.4c-.8.2-1.4.8-1.6 1.6-.4 1.4-.4 4.8-.4 4.8s0 3.4.4 4.8c.2.8.8 1.4 1.6 1.6 1.4.4 6.7.4 6.7.4s5.3 0 6.7-.4c.8-.2 1.4-.8 1.6-1.6.4-1.4.4-4.8.4-4.8s0-3.4-.4-4.8Z"/><path d="m10 9 5 3-5 3Z"/></svg>'
      };
      footer.querySelectorAll('.rk4-footer__socials a').forEach(link => {
        const identity = `${link.textContent} ${link.getAttribute('href') || ''}`.toLowerCase();
        const key = ['instagram', 'tiktok', 'youtube'].find(network => identity.includes(network));
        if (!key) return;
        link.querySelectorAll('svg').forEach(icon => icon.remove());
        link.insertAdjacentHTML('afterbegin', icons[key]);
      });
    });
  };

  const initMarquee = () => {
    document.querySelectorAll('.rk4-marquee:not([data-rk9-marquee-ready])').forEach(marquee => {
      const track = marquee.querySelector('.rk4-marquee__track');
      const groups = track ? [...track.querySelectorAll('.rk4-marquee__group')] : [];
      if (!track || !groups.length) return;
      marquee.dataset.rk9MarqueeReady = 'true';
      const source = groups[0];
      const fillTrack = () => {
        const groupWidth = source.getBoundingClientRect().width;
        const viewportWidth = marquee.getBoundingClientRect().width;
        if (!groupWidth || !viewportWidth) return;
        while (track.scrollWidth < viewportWidth + groupWidth) {
          const clone = source.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.dataset.rk9MarqueeClone = '';
          track.append(clone);
        }
        track.style.setProperty('--rk9-marquee-distance', `${groupWidth * -1}px`);
        track.style.setProperty('--rk9-marquee-duration', `${Math.max(24, groupWidth / 36).toFixed(2)}s`);
      };
      fillTrack();
      window.requestAnimationFrame(fillTrack);
      if ('ResizeObserver' in window) new ResizeObserver(fillTrack).observe(marquee);
    });
  };

  const featuredProducts = () => {
    if (featuredPromise) return featuredPromise;
    const root = window.Shopify?.routes?.root || '/';
    const promo = document.querySelector('.rk4-mega__feature[href*="/collections/"]');
    const handle = promo?.getAttribute('href')?.match(/\/collections\/([^/?#]+)/)?.[1] || 'yeni-sezon';
    featuredPromise = fetch(`${root}collections/${encodeURIComponent(handle)}/products.json?limit=8`, {
      headers: { Accept: 'application/json' }
    }).then(response => {
      if (!response.ok) throw new Error('collection');
      return response.json();
    }).then(data => data.products || []).catch(() => []);
    return featuredPromise;
  };

  const railCard = product => {
    const card = document.createElement('a');
    card.className = 'rk9-product-rail__card';
    card.href = product.url || `/products/${product.handle}`;
    if (imageUrl(product)) {
      const image = document.createElement('img');
      image.src = imageUrl(product);
      image.alt = product.title || '';
      image.loading = 'lazy';
      card.append(image);
    }
    const copy = document.createElement('span');
    const title = document.createElement('strong');
    title.textContent = product.title || 'Rocky';
    const price = document.createElement('small');
    price.textContent = collectionMoney(product.variants?.[0]?.price ?? product.price);
    copy.append(title, price);
    card.append(copy);
    return card;
  };

  const buildRail = (products, context) => {
    const section = document.createElement('section');
    section.className = `rk9-product-rail rk9-product-rail--${context}`;
    section.dataset.rk9ProductRail = context;
    section.innerHTML = `
      <header><div><small>ROCKY SE\u00c7K\u0130S\u0130</small><h3>${context === 'cart' ? 'Sepetine yak\u0131\u015facaklar' : '\u00d6ne \u00e7\u0131kan ayakkab\u0131lar'}</h3></div>
      <div><button type="button" data-rk9-rail-prev aria-label="\u00d6nceki \u00fcr\u00fcn">\u2190</button><button type="button" data-rk9-rail-next aria-label="Sonraki \u00fcr\u00fcn">\u2192</button></div></header>
      <div class="rk9-product-rail__track" data-rk9-rail-track></div>
    `;
    const track = section.querySelector('[data-rk9-rail-track]');
    products.forEach(product => track.append(railCard(product)));
    const move = direction => track.scrollBy({
      left: direction * Math.max(180, track.clientWidth * .78),
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
    section.querySelector('[data-rk9-rail-prev]').addEventListener('click', () => move(-1));
    section.querySelector('[data-rk9-rail-next]').addEventListener('click', () => move(1));
    return section;
  };

  const parseDisplayedMoney = value => {
    const raw = String(value || '').replace(/[^\d,.-]/g, '');
    if (!raw) return 0;
    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    const decimalIndex = Math.max(lastComma, lastDot);
    const decimalLength = decimalIndex >= 0 ? raw.length - decimalIndex - 1 : 0;
    const hasDecimal = decimalIndex >= 0 && decimalLength === 2;
    const whole = hasDecimal ? raw.slice(0, decimalIndex) : raw;
    const decimal = hasDecimal ? raw.slice(decimalIndex + 1) : '';
    const normalized = `${whole.replace(/[^\d-]/g, '')}${decimal ? `.${decimal}` : ''}`;
    return Math.round(Number(normalized || 0) * 100);
  };

  const cartCompareSavings = () => [...document.querySelectorAll('.cart-items__table-row[data-key]')]
    .reduce((total, row) => {
      const details = row.querySelector('.cart-items__details');
      const compare = parseDisplayedMoney(details?.querySelector('.compare-at-price')?.textContent);
      const currentNode = [...(details?.querySelectorAll('span') || [])].find(node => (
        !node.classList.contains('visually-hidden') && parseDisplayedMoney(node.textContent) > 0
      ));
      const current = parseDisplayedMoney(currentNode?.textContent);
      const quantity = Math.max(1, Number(row.querySelector('input[name="updates[]"]')?.value || 1));
      return total + Math.max(0, compare - current) * quantity;
    }, 0);

  const setNodeText = (node, value) => {
    if (node && node.textContent !== value) node.textContent = value;
  };

  const initCartPayPlan = () => {
    const summary = document.querySelector('.cart-drawer__summary');
    const slot = summary?.querySelector('.cart__installments');
    const totalNode = summary?.querySelector('[data-cart-subtotal]');
    if (!slot || !totalNode) return;

    let panel = slot.querySelector('[data-rk9-cart-payplan]');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'rk9-cart-payplan';
      panel.dataset.rk9CartPayplan = '';
      panel.setAttribute('role', 'note');
      panel.innerHTML = `
        <span class="rk9-cart-payplan__badge" aria-hidden="true">3x</span>
        <span class="rk9-cart-payplan__copy">
          <small>KARTINA UYGUN TAKS\u0130T</small>
          <strong><span data-rk9-cart-monthly></span> / ay</strong>
          <em>Uygun kartlarda; kesin se\u00e7enekler \u00f6deme ad\u0131m\u0131nda.</em>
        </span>
        <span class="rk9-cart-payplan__saving" data-rk9-cart-saving hidden></span>`;
      slot.append(panel);
    }

    const total = parseDisplayedMoney(totalNode.getAttribute('value') || totalNode.textContent);
    const saving = cartCompareSavings();
    setNodeText(panel.querySelector('[data-rk9-cart-monthly]'), money(Math.round(total / 3)));
    const savingNode = panel.querySelector('[data-rk9-cart-saving]');
    if (savingNode) {
      setNodeText(savingNode, saving ? `${money(saving)} avantaj` : 'G\u00fcvenli \u00f6deme');
      savingNode.hidden = false;
    }
    setNodeText(summary.querySelector('.cart__checkout-button'), 'G\u00fcvenle \u00f6demeye ge\u00e7');
  };

  const initRails = async () => {
    const menu = document.querySelector('[data-rk4-mobile-menu]');
    const cart = document.querySelector('.cart-drawer__content');
    const menuPending = !menu || menu.querySelector('[data-rk9-product-rail="menu"]') || menu.dataset.rk9RailLoading;
    const cartPending = !cart || cart.querySelector('[data-rk9-product-rail="cart"]') || cart.dataset.rk9RailLoading;
    if (menuPending && cartPending) return;
    if (menu && !menuPending) menu.dataset.rk9RailLoading = 'true';
    if (cart && !cartPending) cart.dataset.rk9RailLoading = 'true';
    const products = await featuredProducts();

    if (menu && !menu.querySelector('[data-rk9-product-rail="menu"]') && products.length) {
      const anchor = menu.querySelector('.rk4-mobile-menu__confidence') || menu.querySelector('.rk9-menu-favorites');
      anchor?.insertAdjacentElement('afterend', buildRail(products.slice(0, 6), 'menu'));
    }
    if (menu) delete menu.dataset.rk9RailLoading;

    const currentCart = document.querySelector('.cart-drawer__content');
    if (currentCart && !currentCart.querySelector('[data-rk9-product-rail="cart"]') && products.length) {
      const rail = buildRail(products.slice(0, 6), 'cart');
      const items = currentCart.querySelector('.cart-drawer__items');
      if (items) items.append(rail);
      else currentCart.append(rail);
    }
    if (currentCart) delete currentCart.dataset.rk9RailLoading;
  };

  const initVariantMedia = () => {
    document.querySelectorAll('[data-rk4-product]:not([data-rk9-media-ready])').forEach(section => {
      const variantsNode = section.querySelector('[data-rk4-variants]');
      const slides = [...section.querySelectorAll('[data-rk4-slide]')];
      const thumbs = [...section.querySelectorAll('[data-rk4-thumb]')];
      const colorGroup = section.querySelector('[data-rk4-option][data-option-kind="color"]');
      if (!variantsNode || !slides.length || !colorGroup) return;
      section.dataset.rk9MediaReady = 'true';
      let variants = [];
      try {
        variants = JSON.parse(variantsNode.textContent || '[]');
      } catch {
        return;
      }
      const colorPosition = Math.max(0, Number(colorGroup.dataset.position || 1) - 1);
      let visibleIds = slides.map(slide => String(slide.dataset.mediaId || ''));
      let activeId = visibleIds.find(id => slides.find(slide => String(slide.dataset.mediaId) === id)?.classList.contains('is-active')) || visibleIds[0];

      const show = mediaId => {
        const allowed = new Set(visibleIds);
        const requested = String(mediaId || '');
        activeId = allowed.has(requested) ? requested : visibleIds[0];
        slides.forEach(slide => {
          const active = String(slide.dataset.mediaId || '') === activeId;
          slide.classList.toggle('is-active', active);
          slide.hidden = !active;
          if (!active) slide.querySelector('video')?.pause?.();
        });
        thumbs.forEach(thumb => {
          const id = String(thumb.dataset.mediaId || '');
          thumb.hidden = !allowed.has(id);
          thumb.classList.toggle('is-active', id === activeId);
        });
        const counter = section.querySelector('[data-rk4-gallery-current]');
        if (counter) {
          counter.textContent = String(Math.max(1, visibleIds.indexOf(activeId) + 1));
          if (counter.nextSibling?.nodeType === Node.TEXT_NODE) counter.nextSibling.nodeValue = ` / ${visibleIds.length}`;
        }
      };

      const mediaFor = variant => {
        const all = slides.map(slide => String(slide.dataset.mediaId || '')).filter(Boolean);
        if (!variant?.mediaId) return all;
        const family = source => {
          const filename = decodeURIComponent(String(source || '').split('?')[0].split('/').pop() || '');
          return filename.replace(/\.[^.]+$/, '').replace(/[_-][a-f0-9]{8}-[a-f0-9-]{27,}$/i, '').replace(/[\s_-]*\d+$/, '').toLocaleLowerCase('tr-TR');
        };
        const selectedSlide = slides.find(slide => String(slide.dataset.mediaId) === String(variant.mediaId));
        const selectedFamily = family(selectedSlide?.dataset.mediaSource);
        const otherColorFamilies = variants.filter(item => item.options?.[colorPosition] !== variant.options?.[colorPosition])
          .map(item => family(slides.find(slide => String(slide.dataset.mediaId) === String(item.mediaId))?.dataset.mediaSource));
        if (selectedFamily.length > 2 && !/^(img|dsc|image|photo|foto)$/i.test(selectedFamily) && !otherColorFamilies.includes(selectedFamily)) {
          const matching = slides.filter(slide => family(slide.dataset.mediaSource) === selectedFamily).map(slide => String(slide.dataset.mediaId));
          if (matching.length) return matching;
        }
        const seen = new Set();
        const anchors = variants.map(item => {
          const color = item.options?.[colorPosition];
          const mediaId = String(item.mediaId || '');
          const index = all.indexOf(mediaId);
          if (!color || index < 0 || seen.has(color)) return null;
          seen.add(color);
          return { color, index };
        }).filter(Boolean).sort((a, b) => a.index - b.index);
        const selected = anchors.find(anchor => anchor.color === variant.options?.[colorPosition]);
        if (!selected) return all;
        const next = anchors.map(anchor => anchor.index).filter(index => index > selected.index);
        return all.slice(selected.index, next.length ? Math.min(...next) : all.length);
      };

      const sync = () => {
        const id = String(section.querySelector('[data-rk4-variant-id]')?.value || '');
        const variant = variants.find(item => String(item.id) === id);
        if (!variant) return;
        visibleIds = mediaFor(variant);
        show(variant.mediaId || activeId);
      };
      const move = direction => {
        const index = Math.max(0, visibleIds.indexOf(activeId));
        show(visibleIds[(index + direction + visibleIds.length) % visibleIds.length]);
      };

      section.addEventListener('rocky:variant-change', sync);
      thumbs.forEach(thumb => thumb.addEventListener('click', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        show(thumb.dataset.mediaId);
      }, true));
      section.querySelector('[data-rk4-gallery-prev]')?.addEventListener('click', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        move(-1);
      }, true);
      section.querySelector('[data-rk4-gallery-next]')?.addEventListener('click', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        move(1);
      }, true);
      section.querySelector('[data-rk4-stage]')?.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      }, true);
      setTimeout(sync, 0);
    });
  };

  const mediaCanPlay = video => {
    const mobileMenu = video.closest('[data-rk4-mobile-menu]');
    if (mobileMenu && mobileMenu.getAttribute('aria-hidden') !== 'false') return false;
    const megaMenu = video.closest('[data-rk4-mega]');
    if (megaMenu) {
      const opener = document.querySelector('[data-rk4-mega-open]');
      if (opener?.getAttribute('aria-expanded') !== 'true') return false;
    }
    const rect = video.getBoundingClientRect();
    const style = window.getComputedStyle(video);
    return style.display !== 'none'
      && style.visibility !== 'hidden'
      && rect.width > 1
      && rect.height > 1
      && rect.bottom >= -320
      && rect.top <= window.innerHeight + 320;
  };

  const hydrateManagedMedia = video => {
    let hydrated = false;
    video.querySelectorAll('source[data-src]').forEach(source => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      hydrated = true;
    });
    if (video.dataset.src) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
      hydrated = true;
    }
    return hydrated;
  };

  const syncManagedMedia = () => {
    document.querySelectorAll('video[data-rk9-media-managed]').forEach(video => {
      if (reduceMotion || !mediaCanPlay(video)) {
        video.pause();
        return;
      }
      if (video.preload !== 'metadata') {
        hydrateManagedMedia(video);
        video.preload = 'metadata';
        video.load();
      }
      video.play().catch(() => {});
    });
  };

  const scheduleManagedMediaSync = () => {
    if (mediaSyncQueued) return;
    mediaSyncQueued = true;
    window.requestAnimationFrame(() => {
      mediaSyncQueued = false;
      syncManagedMedia();
    });
  };

  const initMediaPerformance = () => {
    const selector = 'video.rk4-autoplay:not([data-rk9-media-managed]), [data-rk4-mega] video:not([data-rk9-media-managed]), [data-rk4-mobile-menu] video:not([data-rk9-media-managed])';
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll(selector).forEach(video => {
        video.dataset.rk9MediaManaged = 'true';
        video.autoplay = false;
        video.removeAttribute('autoplay');
        hydrateManagedMedia(video);
        video.preload = 'metadata';
        video.load();
      });
      scheduleManagedMediaSync();
      return;
    }
    if (!mediaObserver) {
      mediaObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting && mediaCanPlay(video)) {
            if (video.preload !== 'metadata') {
              hydrateManagedMedia(video);
              video.preload = 'metadata';
              video.load();
            }
            if (!reduceMotion) video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { rootMargin: '320px 0px', threshold: .01 });
    }

    document.querySelectorAll(selector).forEach(video => {
      video.dataset.rk9MediaManaged = 'true';
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
      video.preload = 'none';
      mediaObserver.observe(video);
    });

    if (!mediaVisibilityReady) {
      const mediaContainers = document.querySelectorAll(
        '.rk4-header, [data-rk4-mega], [data-rk4-mobile-menu]'
      );
      if (mediaContainers.length) {
        mediaVisibilityReady = true;
        mediaVisibilityObserver = new MutationObserver(scheduleManagedMediaSync);
        mediaContainers.forEach(container => {
          mediaVisibilityObserver.observe(container, {
            attributes: true,
            subtree: true,
            attributeFilter: ['class', 'open', 'aria-expanded', 'aria-hidden', 'style']
          });
          container.addEventListener('pointerover', scheduleManagedMediaSync, { passive: true });
          container.addEventListener('pointerout', scheduleManagedMediaSync, { passive: true });
          container.addEventListener('transitionend', scheduleManagedMediaSync);
        });
        window.addEventListener('resize', scheduleManagedMediaSync, { passive: true });
      }
    }
    scheduleManagedMediaSync();
  };

  const repairCartDialogLayer = () => {
    const component = document.querySelector('cart-drawer-component.cart-drawer');
    const dialog = component?.querySelector('.cart-drawer__dialog[open]');
    if (!component || !dialog || component.dataset.rk9ModalRepair === 'pending') return;

    component.dataset.rk9ModalRepair = 'pending';

    // Section morphing preserves the open attribute but drops the dialog from the modal top layer.
    const lockedScrollY = document.body.style.position === 'fixed'
      ? Math.abs(Number.parseFloat(document.body.style.top || '0'))
      : window.scrollY;

    if (dialog.classList.contains('cart-drawer--empty')) {
      dialog.close();
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
      delete component.dataset.rk9ModalRepair;
      return;
    }

    if (dialog.matches(':modal')) {
      delete component.dataset.rk9ModalRepair;
      return;
    }

    window.setTimeout(() => {
      const currentComponent = document.querySelector('cart-drawer-component.cart-drawer');
      const currentDialog = currentComponent?.querySelector('.cart-drawer__dialog[open]');
      if (!currentComponent || !currentDialog) {
        delete component.dataset.rk9ModalRepair;
        return;
      }

      if (currentDialog.classList.contains('cart-drawer--empty')) {
        currentDialog.close();
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
        delete currentComponent.dataset.rk9ModalRepair;
        return;
      }

      if (currentDialog.matches(':modal')) {
        delete currentComponent.dataset.rk9ModalRepair;
        return;
      }

      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
      currentDialog.close();

      if (typeof currentComponent.open === 'function') currentComponent.open();
      else currentDialog.showModal();

      window.requestAnimationFrame(() => {
        if (!currentDialog.matches(':modal')) {
          if (currentDialog.open) currentDialog.close();
          currentDialog.showModal();
          document.body.style.width = '100%';
          document.body.style.position = 'fixed';
          document.body.style.top = `-${lockedScrollY}px`;
        }
        delete currentComponent.dataset.rk9ModalRepair;
      });
    }, 0);
  };

  const observeCartDialogState = () => {
    const dialog = document.querySelector('cart-drawer-component.cart-drawer .cart-drawer__dialog');
    if (!dialog || dialog === observedCartDialog) return;
    cartDialogObserver?.disconnect();
    observedCartDialog = dialog;
    cartDialogObserver = new MutationObserver(repairCartDialogLayer);
    cartDialogObserver.observe(dialog, {
      attributes: true,
      attributeFilter: ['class', 'open']
    });
  };

  const initialize = () => {
    observeCartDialogState();
    repairCartDialogLayer();
    initFavorites();
    initFooter();
    initMarquee();
    void initRails();
    initCartPayPlan();
    initVariantMedia();
    initMediaPerformance();
  };

  document.addEventListener('click', event => {
    if (event.target.closest(
      '[data-rk4-mega-open], [data-rk4-mobile-open], [data-rk4-mobile-close]'
    )) {
      setTimeout(scheduleManagedMediaSync, 0);
    }
    if (event.target.closest(
      '[data-rk4-mobile-open], .rk4-header__cart button, [data-rk4-add], [data-rk4-mobile-add]'
    )) {
      setTimeout(initRails, 0);
    }
    if (event.target.closest('[data-testid="cart-drawer-trigger"]')) {
      window.setTimeout(() => {
        const dialog = document.querySelector('.cart-drawer__dialog[open]');
        const items = dialog?.querySelector('.cart-drawer__items');
        if (items) items.scrollTop = 0;
      }, 0);
    }
    if (event.target.closest('[data-rk9-favorites-open]')) {
      event.preventDefault();
      const dialog = buildFavoritesDialog();
      renderFavorites();
      if (!dialog.open) dialog.showModal();
      return;
    }
    if (event.target.closest('[data-rk5-favorite]')) setTimeout(syncFavoriteSurfaces, 0);
  }, true);
  window.addEventListener('storage', event => {
    if (event.key === favoriteKey) syncFavoriteSurfaces();
  });

  let initializeQueued = false;
  const scheduleInitialize = () => {
    if (initializeQueued) return;
    initializeQueued = true;
    window.requestAnimationFrame(() => {
      initializeQueued = false;
      initialize();
    });
  };

  new MutationObserver(() => {
    repairCartDialogLayer();
    scheduleInitialize();
  }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
